import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { clamp } from 'lodash-es';
import useClickOutside from 'lib/hooks/useClickOutside';
import {
  HEX_COLOR_PATTERN,
  HueSlider,
  SaturationCanvas,
  hexToHsv,
  hsvToHex,
} from './picker-controls';
import {
  rampGradientCss,
  sampleRamp,
  type RampStop,
} from 'components/colormaps';
import styles from './ramp-editor-popover.module.scss';

interface RampEditorPopoverProps {
  /** Shown in the popover header and the trigger's label ("Subsurface Map"). */
  title: string;
  stops: RampStop[];
  /** What "Reset" goes back to. */
  baseStops: RampStop[];
  baseLabel: string;
  /** Called with the new stops whenever an edit is committed. */
  onChange: (stops: RampStop[]) => void;
}

interface KeyedStop extends RampStop {
  key: number;
}

const POPOVER_WIDTH = 280;

/** Selectors a host modal treats as its own, since the popover is portalled out. */
export const FLOATING_MENU_SELECTORS = ['[data-floating-menu]'];
const VIEWPORT_MARGIN = 8;

/**
 * Clamps a stop position to the ramp.
 * @param {number} offset The position, 0–100.
 * @return {number} The clamped position.
 */
const clampOffset = (offset: number): number => clamp(offset, 0, 100);

/**
 * Whether two stop lists paint the same ramp.
 * @param {RampStop[]} a One list.
 * @param {RampStop[]} b The other.
 * @return {boolean} True when they match stop for stop.
 */
export const sameStops = (a: RampStop[], b: RampStop[]): boolean => {
  const sort = (stops: RampStop[]) =>
    [...stops].sort((x, y) => x.offset - y.offset);
  const [left, right] = [sort(a), sort(b)];
  return (
    left.length === right.length &&
    left.every(
      (stop, index) =>
        Math.abs(stop.offset - right[index].offset) < 0.01 &&
        stop.color.toLowerCase() === right[index].color.toLowerCase()
    )
  );
};

/**
 * Gives each stop a stable key, so a handle keeps its identity while dragged.
 * @param {RampStop[]} stops The stops.
 * @return {KeyedStop[]} The stops with keys.
 */
const withKeys = (stops: RampStop[]): KeyedStop[] =>
  stops.map((stop, key) => ({ ...stop, key }));

/**
 * Drops the keys and orders stops low to high.
 * @param {KeyedStop[]} stops The keyed stops.
 * @return {RampStop[]} Plain stops.
 */
const withoutKeys = (stops: KeyedStop[]): RampStop[] =>
  [...stops]
    .sort((a, b) => a.offset - b.offset)
    .map(({ offset, color }) => ({ offset, color }));

/**
 * The design system's Ramp Swatch: a 48×24 ramp that opens its profile editor.
 * Click the bar to add a stop, drag or arrow-key a handle (or type its position)
 * to move it, edit its colour with the shared picker controls, Remove the selected
 * stop (two minimum). Transparency belongs to the layer, so stops have none.
 * Closes on an outside click or Escape.
 * @param {RampEditorPopoverProps} props The ramp, its reset target and a change handler.
 * @return {JSX.Element} The swatch trigger and, when open, the popover.
 */
export const RampEditorPopover = ({
  title,
  stops,
  baseStops,
  baseLabel,
  onChange,
}: RampEditorPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<KeyedStop[]>(() => withKeys(stops));
  const [selectedKey, setSelectedKey] = useState(0);
  const [hexInput, setHexInput] = useState(stops[0]?.color ?? '');
  // The selected stop's colour as HSV, kept here rather than re-derived from its hex:
  // at black or grey the hex carries no hue, so re-deriving it would snap to red.
  const [hsv, setHsvState] = useState<[number, number, number]>(() => {
    return stops[0] ? hexToHsv(stops[0].color) : [0, 0, 0];
  });
  const [position, setPosition] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const nextKey = useRef(stops.length);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Selects a stop and loads its colour into the HEX field and the SV/hue controls.
  const selectStop = (stop: KeyedStop) => {
    setSelectedKey(stop.key);
    setHexInput(stop.color);
    setHsvState(hexToHsv(stop.color));
  };

  // Commits whatever is still pending (a keyboard or touch edit, a typed position)
  // so closing never throws an edit away, then hands focus back to the swatch.
  const closeRef = useRef<(returnFocus: boolean) => void>(() => undefined);
  closeRef.current = (returnFocus: boolean) => {
    commit();
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  // Only fires while open: the hook needs every ref mounted, and the popover isn't.
  const outsideRefs = useMemo(() => [popoverRef, triggerRef], []);
  const onClickOutside = useCallback(() => closeRef.current(false), []);
  useClickOutside({ refs: outsideRefs, onClickOutside });

  useEffect(() => {
    if (!open) return undefined;
    // Escape only while focus is in the editor or on its swatch.
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      const target = event.target as Node;
      const inEditor =
        popoverRef.current?.contains(target) ||
        triggerRef.current?.contains(target);
      if (event.key === 'Escape' && inEditor) closeRef.current(true);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Opens below the swatch; flips above it when there isn't room, and scrolls when
  // neither side fits.
  useLayoutEffect(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    const popover = popoverRef.current;
    if (!open || !trigger || !popover) return;
    const height = popover.offsetHeight;
    const roomBelow = window.innerHeight - trigger.bottom - VIEWPORT_MARGIN;
    const roomAbove = trigger.top - VIEWPORT_MARGIN;
    if (height <= roomBelow) return;
    const top =
      height <= roomAbove || roomAbove > roomBelow
        ? Math.max(VIEWPORT_MARGIN, trigger.top - height - 4)
        : trigger.bottom + 4;
    setPosition((current) => ({
      ...current,
      top,
      maxHeight: window.innerHeight - top - VIEWPORT_MARGIN,
    }));
  }, [open]);

  const sorted = useMemo(
    () => [...draft].sort((a, b) => a.offset - b.offset),
    [draft]
  );
  const selected = draft.find((stop) => stop.key === selectedKey) ?? sorted[0];

  // Focus the selected handle when the editor opens, so it is keyboard-usable at once.
  useEffect(() => {
    if (!open) return;
    popoverRef.current
      ?.querySelector<HTMLElement>('[aria-current="true"]')
      ?.focus();
  }, [open]);

  // Commit a pending edit when a key or pointer is released inside the editor: the
  // hue slider only commits itself on mouse up, so keyboard and touch edits land here.
  const commitPending = () => commit();

  const draftStops = useMemo(() => withoutKeys(draft), [draft]);
  const gradient = rampGradientCss(draftStops);
  // Closed, the swatch shows the applied ramp (another control or a reset may have
  // changed it); open, the draft being edited.
  const swatchGradient = open ? gradient : rampGradientCss(stops);
  const changed = !sameStops(draftStops, baseStops);

  // Every path commits through here, so a blur or slider release that changed
  // nothing never reaches onChange (which would create a colour rule).
  const commit = (next: KeyedStop[] = draftRef.current) => {
    const nextStops = withoutKeys(next);
    if (!sameStops(nextStops, stops)) onChange(nextStops);
  };

  const updateStop = (
    key: number,
    patch: Partial<RampStop>,
    commitNow = true
  ) => {
    const next = draftRef.current.map((stop) => {
      if (stop.key !== key) return stop;
      return { ...stop, ...patch };
    });
    setDraft(next);
    if (patch.color) setHexInput(patch.color);
    if (commitNow) commit(next);
  };

  const offsetAt = (clientX: number): number => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return clampOffset(((clientX - rect.left) / rect.width) * 100);
  };

  const addStop = (event: MouseEvent) => {
    const offset = offsetAt(event.clientX);
    // A new stop takes the colour already showing there, so adding one never
    // changes the ramp until it is moved or recoloured.
    const stop: KeyedStop = {
      offset,
      color: sampleRamp(withoutKeys(draftRef.current), offset),
      key: nextKey.current,
    };
    nextKey.current += 1;
    const next = [...draftRef.current, stop];
    setDraft(next);
    selectStop(stop);
    commit(next);
  };

  const startDrag = (key: number) => (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const stop = draftRef.current.find((each) => each.key === key);
    if (stop) selectStop(stop);
    const onMove = (move: globalThis.MouseEvent) =>
      updateStop(key, { offset: offsetAt(move.clientX) }, false);
    const onUp = (up: globalThis.MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      updateStop(key, { offset: offsetAt(up.clientX) });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const nudge = (key: number) => (event: KeyboardEvent) => {
    const step = { ArrowLeft: -1, ArrowRight: 1 }[
      event.key as 'ArrowLeft' | 'ArrowRight'
    ];
    if (step === undefined) return;
    event.preventDefault();
    const stop = draftRef.current.find((each) => each.key === key);
    if (stop) updateStop(key, { offset: clampOffset(stop.offset + step) });
  };

  const removeSelected = () => {
    if (!selected || draft.length <= 2) return;
    const next = draft.filter((stop) => stop.key !== selected.key);
    setDraft(next);
    selectStop(next[0]);
    commit(next);
  };

  const reset = () => {
    const keyed = withKeys(baseStops);
    nextKey.current = keyed.length;
    setDraft(keyed);
    if (keyed[0]) selectStop(keyed[0]);
    commit(keyed);
  };

  const toggle = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: rect.bottom + 4,
        left: clamp(
          rect.left,
          VIEWPORT_MARGIN,
          window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN
        ),
      });
    }
    if (open) {
      closeRef.current(false);
      return;
    }
    // Open on the applied ramp; an unchanged one keeps its keys and selection.
    if (!sameStops(withoutKeys(draftRef.current), stops)) {
      const keyed = withKeys(stops);
      setDraft(keyed);
      nextKey.current = keyed.length;
      if (keyed[0]) selectStop(keyed[0]);
    }
    setOpen(true);
  };

  const [hue, sat, val] = hsv;
  const setHsv = (
    nextHue: number,
    nextSat: number,
    nextVal: number,
    commitNow: boolean
  ) => {
    if (!selected) return;
    setHsvState([nextHue, nextSat, nextVal]);
    updateStop(
      selected.key,
      { color: hsvToHex(nextHue, nextSat, nextVal) },
      commitNow
    );
  };

  return (
    <div className={styles.wrapper}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.swatch}
        // The ramp is data; it can only be painted inline.
        style={{ background: swatchGradient }}
        onClick={toggle}
        aria-label={`Edit the ${title} colour profile`}
        aria-expanded={open}
      />
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className={styles.popover}
            // Fixed under the trigger so the scrolling sidebar can't clip it.
            style={position}
            role="dialog"
            aria-label={`${title} colour profile`}
            // Portalled outside any modal, so a host modal keeps focus here and
            // does not read clicks in it as outside clicks.
            data-floating-menu=""
            onKeyUp={commitPending}
            onPointerUp={commitPending}
          >
            <div className={styles.header}>
              <span className={styles.title}>{title}</span>
              <button
                type="button"
                className={styles.resetButton}
                onClick={reset}
                disabled={!changed}
              >
                {`Reset to ${baseLabel}`}
              </button>
            </div>

            <div
              ref={barRef}
              className={styles.bar}
              style={{ background: gradient }}
              onMouseDown={addStop}
              title="Click to add a stop"
              aria-hidden
            />
            <div className={styles.track}>
              {sorted.map((stop) => (
                <button
                  key={stop.key}
                  type="button"
                  className={classNames(styles.handle, {
                    [styles.handleSelected]: stop.key === selected?.key,
                  })}
                  // Position is data, so it can only be set inline.
                  style={{ left: `${stop.offset}%` }}
                  onMouseDown={startDrag(stop.key)}
                  onKeyDown={nudge(stop.key)}
                  onFocus={() => selectStop(stop)}
                  aria-current={stop.key === selected?.key}
                  aria-label={`Stop at ${Math.round(stop.offset)}%, ${
                    stop.color
                  }`}
                >
                  <span
                    className={styles.handleChip}
                    style={{ background: stop.color }}
                  />
                </button>
              ))}
            </div>

            {selected && (
              <>
                <SaturationCanvas
                  hue={hue}
                  sat={sat}
                  val={val}
                  onSatValChange={(nextSat, nextVal) =>
                    setHsv(hue, nextSat, nextVal, false)
                  }
                  onSatValChangeEnd={(nextSat, nextVal) =>
                    setHsv(hue, nextSat, nextVal, true)
                  }
                />
                <HueSlider
                  hue={hue}
                  onHueChange={(nextHue) => setHsv(nextHue, sat, val, false)}
                  onHueChangeEnd={() => commit()}
                />
                <div className={styles.stopRow}>
                  <span className={styles.hexLabel}>HEX</span>
                  <input
                    className={styles.hexInput}
                    value={hexInput}
                    spellCheck={false}
                    aria-label="Stop colour"
                    onChange={(event) => {
                      const value = event.target.value;
                      setHexInput(value);
                      if (HEX_COLOR_PATTERN.test(value)) {
                        // A grey or black keeps the hue it had.
                        const [nextHue, nextSat, nextVal] = hexToHsv(value);
                        setHsvState([
                          nextSat === 0 || nextVal === 0 ? hue : nextHue,
                          nextSat,
                          nextVal,
                        ]);
                        updateStop(selected.key, { color: value });
                      }
                    }}
                    onBlur={() => setHexInput(selected.color)}
                  />
                  {/* Position, not opacity: a ramp's stops have no alpha. */}
                  <input
                    className={styles.positionInput}
                    type="number"
                    min={0}
                    max={100}
                    aria-label="Stop position"
                    value={Math.round(selected.offset)}
                    onChange={(event) => {
                      if (event.target.value === '') return;
                      updateStop(
                        selected.key,
                        { offset: clampOffset(Number(event.target.value)) },
                        false
                      );
                    }}
                    onBlur={() => commit()}
                  />
                  <span className={styles.percent}>%</span>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={removeSelected}
                    disabled={draft.length <= 2}
                    title={
                      draft.length <= 2
                        ? 'A ramp needs at least two stops'
                        : 'Remove this stop'
                    }
                  >
                    Remove
                  </button>
                </div>
              </>
            )}

            <ul className={styles.stopList}>
              {sorted.map((stop) => (
                <li key={stop.key}>
                  <button
                    type="button"
                    className={classNames(styles.stopListRow, {
                      [styles.stopListRowSelected]: stop.key === selected?.key,
                    })}
                    onClick={() => selectStop(stop)}
                  >
                    <span
                      className={styles.stopListChip}
                      style={{ background: stop.color }}
                    />
                    <span className={styles.stopListPosition}>
                      {`${Math.round(stop.offset)}%`}
                    </span>
                    <span className={styles.stopListHex}>
                      {stop.color.toUpperCase()}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};
